import { Exception, UnprocessableEntityException } from '@arpeggi/shared'
import * as Dialog from '@radix-ui/react-dialog'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { observer } from 'mobx-react-lite'
import React, { FC } from 'react'
import ReactGA from 'react-ga'
import { useArpDraftsAPI } from '../../../api/v2/ArpDraftsAPI'
import { useAuth } from '../../../hooks/useAuth'
import { useCreateDraft, useUpdateDraft } from '../../../queries/drafts'
import { useTreeState } from '../../../stores/Root'
import { requiresUserLoggedIn } from '../../AuthFlow/AuthFlowModal'
import { userIsLoggedIn } from '../../core/Authentication'
import { Box, Row } from '../../core/Components'
import { FieldLabel } from '../../core/FieldLabel'
import LoadingAnimation from '../../core/LoadingAnimation'
import { Modal } from '../../core/Modal'
import { Subtitle } from '../../core/Subtitle'
import { Title } from '../../core/Title'
import { StudioActivityBarIcon } from '../StudioActivityBar'

const MAX_SONG_NAME_CHARS = 50
export interface StudioSaveComponentBodyProps {
  userIsLeavingStudio?: boolean
  next?: () => void
}

export const StudioSaveComponentBody: FC<StudioSaveComponentBodyProps> = observer(
  ({ userIsLeavingStudio, next }) => {
    const createDraft = useCreateDraft()
    const updateDraft = useUpdateDraft()
    const { getDrafts } = useArpDraftsAPI()

    const [draftName, setDraftName] = React.useState('')
    const [isConfirm, setIsConfirm] = React.useState(false)
    const [isSubmitting, setIsSubmitting] = React.useState(false)
    const [songNameError, setSongNameError] = React.useState(null)
    const [songNameTouched, setSongNameTouched] = React.useState(false)
    const { studio } = useTreeState()
    const flags = useFlags()
    const remixedStemsNotAllowed =
      !flags.allowDraftsWithRemixes &&
      studio?.song?.stems.reduce((previousValue, stem) => {
        return stem.isRemixed || previousValue
      }, false)

    const validateSongName = (name) => {
      if (name.length === 0) setSongNameError('Minimum 1 character')
      else if (name.length >= MAX_SONG_NAME_CHARS)
        setSongNameError(`Maximum ${MAX_SONG_NAME_CHARS} characters`)
      else setSongNameError(null)
      setSongNameTouched(true)
    }

    const onSaveSuccess = () => {
      studio.song.name = draftName
      ReactGA.event({
        category: 'User',
        action: 'Draft Saved'
      })
      window.analytics.track('Draft Saved')

      studio.songChangedSinceSave = false
      if (userIsLeavingStudio) next()

      setIsSubmitting(false)
    }

    const onSubmit = async () => {
      setIsSubmitting(true)

      if (!isConfirm) {
        createDraft.mutate(
          { name: draftName, arp: studio.song.serializeSongForStorage() },
          {
            onSuccess: () => onSaveSuccess(),
            onError: (error: any) => {
              const errorData: Exception | UnprocessableEntityException = error.response.data
              if (
                'errors' in errorData &&
                errorData.errors.find((x) => x.field === 'name' && x.rule === 'unique')
              ) {
                setIsConfirm(true)
                setIsSubmitting(false)
              }
            }
          }
        )
      } else {
        const drafts = await getDrafts({ perPage: 1, name: draftName })
        const currentDraft = drafts.data[0]

        updateDraft.mutate(
          {
            id: currentDraft.id,
            dto: { name: draftName, arp: studio.song.serializeSongForStorage() }
          },
          {
            onSuccess: () => onSaveSuccess()
          }
        )
      }
    }

    React.useEffect(() => {
      setDraftName(studio.song.name)
    }, [studio.song.name])

    const alreadySaved = !studio.songChangedSinceSave && studio.song.name === draftName
    let innerText = ''
    if (alreadySaved) innerText = 'Your Song is Saved'
    else if (isConfirm) innerText = 'Confirm'
    else innerText = 'Save Draft'

    return (
      <Box css={{ width: '400px' }}>
        <Title>
          <img
            src={
              userIsLeavingStudio
                ? '/static/icons/studio_danger.svg'
                : '/static/icons/studio_save.svg'
            }
            className="mb-1"
            style={{ height: '24px', width: '24px' }}
          />
          <p>{userIsLeavingStudio ? 'Close draft without saving?' : 'Save'}</p>
        </Title>
        <Box className="fs-14">
          {remixedStemsNotAllowed && (
            <>
              <Subtitle>
                Saving drafts containing <b>remixed stems</b> will not be enabled until Arpeggi V2
                switches to Mainnet. For now, please only save drafts with original stems. Thanks
                for your patience!
              </Subtitle>
              <Row justifyContentEnd css={{ marginTop: '32px' }}>
                <Dialog.Close
                  className="studio-modal-button-outline me-3"
                  style={{ width: '50%' }}
                  onClick={next}
                >
                  {"Don't Save"}
                </Dialog.Close>
                <Dialog.Close className="studio-modal-button-fill me-3" style={{ width: '50%' }}>
                  Cancel
                </Dialog.Close>
              </Row>
            </>
          )}
          {!remixedStemsNotAllowed && (
            <Box>
              {!userIsLeavingStudio && <Subtitle>Save this draft to your account.</Subtitle>}
              <FieldLabel>Title of draft</FieldLabel>
              <input
                className="studio-modal-input w-100"
                value={draftName}
                onBlur={(e) => validateSongName(e.target.value)}
                onChange={(e) => {
                  setDraftName(e.target.value)
                  validateSongName(e.target.value)
                  setIsConfirm(false)
                }}
              />
              <p className="error-text mb-0">{songNameTouched && songNameError && songNameError}</p>
              <Row verticalCenter className="muted" style={{ height: isConfirm ? '70px' : '30px' }}>
                {isConfirm && (
                  <>
                    <img
                      className="ps-1 me-2"
                      src="/static/icons/studio_danger.svg"
                      style={{ height: '16px', width: '16px' }}
                    />
                    <span>By saving you will overwrite your previous draft.</span>
                  </>
                )}
              </Row>
              <Row>
                {userIsLeavingStudio && (
                  <Dialog.Close
                    className="studio-modal-button-outline me-3"
                    style={{ width: '50%' }}
                    onClick={next}
                  >
                    Don't Save
                  </Dialog.Close>
                )}
                <Dialog.Close className="studio-modal-button-outline me-3" style={{ width: '50%' }}>
                  Cancel
                </Dialog.Close>
                <button
                  type="button"
                  className="studio-modal-button-fill d-flex justify-content-center align-items-center"
                  style={{ width: '50%' }}
                  disabled={isSubmitting || songNameError !== null || alreadySaved}
                  onClick={onSubmit}
                >
                  {isSubmitting ? <LoadingAnimation light small /> : innerText}
                </button>
              </Row>
            </Box>
          )}
        </Box>
      </Box>
    )
  }
)

const Trigger: FC = () => {
  const { user } = useAuth()

  const onClick = (e) => {
    if (!userIsLoggedIn(user)) {
      ReactGA.event({
        category: 'Studio',
        action: 'Clicked Save Draft in Activity Bar',
        label: 'Not Logged In'
      })
      return
    } else {
      ReactGA.event({
        category: 'Studio',
        action: 'Clicked Save Draft in Activity Bar',
        label: 'Logged In'
      })
    }
  }

  return (
    <StudioActivityBarIcon
      title="Save Draft"
      onClick={onClick}
      src="/static/icons/save_drawer_icon.svg"
    />
  )
}

const StudioSaveComponent: FC<StudioSaveComponentBodyProps> = (props) => {
  const { user, isUserLoading } = useAuth()
  const trigger = requiresUserLoggedIn(<Trigger />)

  if (!user || isUserLoading) {
    return <>{trigger}</>
  }

  return (
    <Modal
      trigger={
        <Box>
          <Trigger />
        </Box>
      }
    >
      <StudioSaveComponentBody {...props} />
    </Modal>
  )
}

export default observer(StudioSaveComponent)
